import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import AutumnalDecorations from './AutumnalDecorations';
import PhotoCollage from './PhotoCollage';
import type { Photo } from '../types';

export default function PostWeddingView() {
  const navigate = useNavigate();
  const { 
    setPhotos, 
    setIsUploadModalOpen, 
    userRole,
    adminBypass,
    setAdminBypass,
    isWeddingDay 
  } = useApp();
  const [isLoading, setIsLoading] = useState(true);

  // Fetch initial photos
  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const response = await fetch('/api/photos');
        const data = await response.json();
        if (data.photos) {
          setPhotos(data.photos as Photo[]);
        }
      } catch (error) {
        console.error('Failed to fetch photos:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPhotos();
  }, [setPhotos]);

  const handleExitPreview = () => {
    setAdminBypass(false);
  };

  return (
    <div className="min-h-screen flex flex-col relative">
      <AutumnalDecorations intensity="light" />
      
      {/* Header */}
      <header className="relative z-20 py-4 px-4 md:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg className="w-8 h-8 text-autumn-gold" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C8 6 4 9 4 14c0 5 3.5 8 8 8s8-3 8-8c0-5-4-8-8-12z" />
            </svg>
            <div>
              <h1 className="text-xl md:text-2xl font-display font-semibold text-autumn-brown">
                Nicola & Stuart
              </h1>
              <p className="text-xs md:text-sm font-body text-autumn-light-brown">
                October 3rd, 2026
              </p>
            </div>
          </div>

          {/* Admin preview banner */}
          {adminBypass && !isWeddingDay && (
            <div className="flex items-center gap-3 bg-autumn-orange/10 px-4 py-2 rounded-lg">
              <span className="text-sm font-body text-autumn-orange">
                Preview Mode
              </span>
              <button
                onClick={handleExitPreview}
                className="text-xs font-body text-autumn-orange hover:text-autumn-red underline"
              >
                Exit
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main collage area */}
      <main className="flex-1 relative z-10 flex flex-col">
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 relative">
                <div className="absolute inset-0 rounded-full border-4 border-autumn-gold/20" />
                <div className="absolute inset-0 rounded-full border-4 border-autumn-gold border-t-transparent animate-spin" />
              </div>
              <p className="font-body text-autumn-light-brown">Loading memories...</p>
            </div>
          </div>
        ) : (
          <PhotoCollage />
        )}
      </main>

      {/* Bottom action bar */}
      <footer className="relative z-20 py-4 px-4 md:px-8 bg-gradient-to-t from-autumn-cream via-autumn-cream to-transparent">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="btn-autumn flex items-center gap-2 min-w-[200px] justify-center"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Share a Photo
          </button>

          <button
            onClick={() => navigate('/gallery')}
            className="btn-outline flex items-center gap-2 min-w-[200px] justify-center"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            View All Photos
          </button>

          {userRole === 'admin' && (
            <button
              onClick={() => navigate('/gallery?admin=true')}
              className="text-autumn-orange hover:text-autumn-red font-body text-sm underline transition-colors"
            >
              Admin Panel
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}

